// Cave carvers (GEN-6). Spaghetti tunnels from two 3D noises (air where both are
// near zero) plus large "cheese" cavities below y40. E-6 flood check: a cell is
// never carved if it touches water (within the chunk), so caves can't drain the
// oceans — a 1-block stone shell is left under the sea.
//
// Two optional passes ride on top (see caves.ts): aquifers flood carved cells that
// sit under their column's water table, and lush caves dress a carved cavern with
// moss, glow lichen and berry-bearing vines. Both change only what a carved cell
// BECOMES, never which cells are carved, and a Carver built without them produces
// byte-identical chunks to before.
import { WORLD, LUSH, AQUIFER } from '../../config';
import { Simplex } from '../../engine/noise';
import { Rng, defineSalt } from '../../engine/rng';
import { STATE_AIR, sectionIndexForY } from '../../engine/section';
import type { Chunk } from '../../engine/chunk';
import type { CaveFeatures } from './caves';

const S_LUSH_DECOR = defineSalt('cave-lush-decor');

const SPAGHETTI_FREQ = 0.019;
const SPAGHETTI_THRESHOLD = 0.020;
const CHEESE_FREQ = 0.011;
const CHEESE_THRESHOLD = 0.62;
const noBlock = (): boolean => false;

export class Carver {
  private readonly a: Simplex;
  private readonly b: Simplex;
  private readonly cheese: Simplex;

  private readonly decorRng: Rng;

  constructor(
    seed: number | string | bigint,
    private readonly carvable: Uint8Array,
    private readonly waterState: number,
    private readonly features?: CaveFeatures,
  ) {
    this.a = new Simplex(`${seed}#cave_a`);
    this.b = new Simplex(`${seed}#cave_b`);
    this.cheese = new Simplex(`${seed}#cave_cheese`);
    this.decorRng = new Rng(seed).fork(S_LUSH_DECOR);
  }

  private nearWater(chunk: Chunk, lx: number, y: number, lz: number): boolean {
    if (chunk.getBlockState(lx, y + 1, lz) === this.waterState) return true;
    if (chunk.getBlockState(lx, y - 1, lz) === this.waterState) return true;
    if (lx > 0 && chunk.getBlockState(lx - 1, y, lz) === this.waterState) return true;
    if (lx < 15 && chunk.getBlockState(lx + 1, y, lz) === this.waterState) return true;
    if (lz > 0 && chunk.getBlockState(lx, y, lz - 1) === this.waterState) return true;
    if (lz < 15 && chunk.getBlockState(lx, y, lz + 1) === this.waterState) return true;
    return false;
  }

  carve(chunk: Chunk): void {
    const bx = chunk.cx * WORLD.SECTION, bz = chunk.cz * WORLD.SECTION;
    for (let lx = 0; lx < WORLD.SECTION; lx++) {
      for (let lz = 0; lz < WORLD.SECTION; lz++) {
        const surf = chunk.surfaceY(lx, lz);
        if (surf <= WORLD.MIN_Y) continue;
        const wx = bx + lx, wz = bz + lz;
        const top = Math.min(surf - 2, WORLD.MAX_Y);
        for (let y = WORLD.MIN_Y + 5; y <= top; y++) {
          // Only descend into loaded sections that hold this column.
          if (!chunk.sections[sectionIndexForY(y)]) continue;
          const s = chunk.getBlockState(lx, y, lz);
          if (this.carvable[s] !== 1) continue;
          const n1 = this.a.noise3(wx * SPAGHETTI_FREQ, y * SPAGHETTI_FREQ * 1.7, wz * SPAGHETTI_FREQ);
          const n2 = this.b.noise3(wx * SPAGHETTI_FREQ, y * SPAGHETTI_FREQ * 1.7, wz * SPAGHETTI_FREQ);
          const spaghetti = n1 * n1 + n2 * n2 < SPAGHETTI_THRESHOLD;
          const cheese = y < 40 && this.cheese.noise3(wx * CHEESE_FREQ, y * CHEESE_FREQ * 1.4, wz * CHEESE_FREQ) > CHEESE_THRESHOLD;
          if (!spaghetti && !cheese) continue;
          if (this.nearWater(chunk, lx, y, lz)) continue; // E-6
          chunk.setBlockState(lx, y, lz, STATE_AIR, noBlock);
        }
      }
    }
    // Aquifers fill AFTER the carve, never during it: the E-6 near-water guard
    // reads live chunk state, so flooding inline would make each newly-filled cell
    // veto its own neighbours' carve and seal the cavern off above its own water.
    this.floodAquifers(chunk);
    this.dressLushCaves(chunk);
  }

  /**
   * Is this column far enough from any open water to put a free water SURFACE in?
   *
   * E-6 — a carved cell is never emptied next to water, so a cave can't open into
   * the ocean and drain it — is what keeps the sea where it is, and any standing
   * water a later pass introduces would break it near the coast. Requiring a column
   * to stand LAND_MARGIN above sea level AND hold no water of its own keeps those
   * passes off the shore entirely, so their water and sea water are never the same
   * body. Both the aquifer flood and the lush clay pools ride on this.
   */
  private inlandAndDry(chunk: Chunk, lx: number, lz: number): boolean {
    if (chunk.surfaceY(lx, lz) < WORLD.SEA_LEVEL + AQUIFER.LAND_MARGIN) return false;
    return !this.columnHasWater(chunk, lx, lz);
  }

  /** Does this column already hold generated water (a lake, a river, the sea)? */
  private columnHasWater(chunk: Chunk, lx: number, lz: number): boolean {
    const top = Math.min(chunk.surfaceY(lx, lz), WORLD.MAX_Y);
    for (let y = WORLD.MIN_Y + 5; y <= top; y++) {
      if (!chunk.sections[sectionIndexForY(y)]) continue;
      if (chunk.getBlockState(lx, y, lz) === this.waterState) return true;
    }
    return false;
  }

  /**
   * Fill every carved cell at or below its column's water table (GEN-6 aquifers).
   *
   * Well-inland columns only, and never above AQUIFER.MAX_Y. The E-6 rule — a
   * carved cell is never emptied next to water, so a cave can't open into the ocean
   * and drain it — is what keeps the sea where it is, and an aquifer's free surface
   * would break it near the coast. Requiring a column to stand LAND_MARGIN above
   * sea level AND hold no water of its own keeps the pass off the shore entirely,
   * so aquifer water and sea water are never the same body and E-6 is untouched.
   */
  private floodAquifers(chunk: Chunk): void {
    const water = this.features?.ids.water;
    if (water === undefined || !this.features) return;
    const bx = chunk.cx * WORLD.SECTION, bz = chunk.cz * WORLD.SECTION;
    for (let lx = 0; lx < WORLD.SECTION; lx++) {
      for (let lz = 0; lz < WORLD.SECTION; lz++) {
        if (!this.inlandAndDry(chunk, lx, lz)) continue;
        const wx = bx + lx, wz = bz + lz;
        const table = Math.min(this.features.waterTableY(wx, wz), AQUIFER.MAX_Y);
        for (let y = WORLD.MIN_Y + 5; y <= table; y++) {
          if (!chunk.sections[sectionIndexForY(y)]) continue;
          if (chunk.getBlockState(lx, y, lz) !== STATE_AIR) continue;
          chunk.setBlockState(lx, y, lz, water, () => false);
        }
      }
    }
  }

  /**
   * Dress the carved caverns of a lush column: moss (sometimes carpeted) on a
   * cave floor, and glow lichen or a hanging berry vine on the ceiling above it.
   * Runs after the carve so it sees the finished cavity, and only ever writes into
   * air — an aquifer-flooded cell is left as water.
   */
  private dressLushCaves(chunk: Chunk): void {
    const lush = this.features?.ids.lush;
    if (!lush || !this.features) return;
    const bx = chunk.cx * WORLD.SECTION, bz = chunk.cz * WORLD.SECTION;
    const rng = this.decorRng.forkAt(S_LUSH_DECOR, chunk.cx, chunk.cz);
    const solid = (lx: number, y: number, lz: number): boolean => {
      const st = chunk.getBlockState(lx, y, lz);
      return st !== STATE_AIR && st !== this.waterState;
    };
    for (let lx = 0; lx < WORLD.SECTION; lx++) {
      for (let lz = 0; lz < WORLD.SECTION; lz++) {
        const wx = bx + lx, wz = bz + lz;
        if (!this.features.isLush(wx, wz)) continue;
        const surf = chunk.surfaceY(lx, lz);
        const top = Math.min(surf - 2, WORLD.MAX_Y);
        for (let y = WORLD.MIN_Y + 5; y <= top; y++) {
          if (!chunk.sections[sectionIndexForY(y)]) continue;
          if (chunk.getBlockState(lx, y, lz) !== STATE_AIR) continue;
          // Floor: mossy stone under an open cell, sometimes carpeted — or, rarely,
          // the seed of a clay pool, or an azalea bush.
          if (solid(lx, y - 1, lz) && this.carvable[chunk.getBlockState(lx, y - 1, lz)] === 1) {
            chunk.setBlockState(lx, y - 1, lz, lush.moss, () => true);
            // A pool holds standing water, so it is bound by the same inland/dry
            // rule as the aquifer flood — see `inlandAndDry` for why E-6 demands it.
            if (lush.clay !== undefined && rng.nextInt(LUSH.CLAY_POOL_ONE_IN) === 0
                && this.inlandAndDry(chunk, lx, lz)) {
              this.sinkClayPool(chunk, lx, y, lz, lush, rng);
            } else if (lush.azalea !== undefined && rng.nextInt(LUSH.AZALEA_ONE_IN) === 0) {
              const flowering = lush.floweringAzalea !== undefined && rng.nextInt(LUSH.FLOWERING_ONE_IN) === 0;
              chunk.setBlockState(lx, y, lz, flowering ? lush.floweringAzalea! : lush.azalea, () => false);
            } else if (rng.nextInt(LUSH.CARPET_ONE_IN) === 0) {
              chunk.setBlockState(lx, y, lz, lush.mossCarpet, () => false);
            }
            continue;
          }
          // Ceiling: rooted dirt trailing roots, a spore blossom, a lichen crust, or
          // a vine hanging down into the cavern.
          if (!solid(lx, y + 1, lz)) continue;
          if (lush.rootedDirt !== undefined && this.carvable[chunk.getBlockState(lx, y + 1, lz)] === 1
              && rng.nextInt(LUSH.ROOTED_ONE_IN) === 0) {
            chunk.setBlockState(lx, y + 1, lz, lush.rootedDirt, () => true);
            if (lush.hangingRoots !== undefined) {
              const len = 1 + rng.nextInt(LUSH.ROOT_MAX);
              for (let i = 0; i < len; i++) {
                const ry = y - i;
                if (ry <= WORLD.MIN_Y || chunk.getBlockState(lx, ry, lz) !== STATE_AIR) break;
                chunk.setBlockState(lx, ry, lz, lush.hangingRoots, () => false);
              }
            }
          } else if (lush.sporeBlossom !== undefined && rng.nextInt(LUSH.SPORE_ONE_IN) === 0) {
            chunk.setBlockState(lx, y, lz, lush.sporeBlossom, () => false);
          } else if (rng.nextInt(LUSH.LICHEN_ONE_IN) === 0) {
            chunk.setBlockState(lx, y, lz, lush.glowLichen, () => false);
          } else if (rng.nextInt(LUSH.VINE_ONE_IN) === 0) {
            const len = 1 + rng.nextInt(LUSH.VINE_MAX);
            for (let i = 0; i < len; i++) {
              const vy = y - i;
              if (vy <= WORLD.MIN_Y || chunk.getBlockState(lx, vy, lz) !== STATE_AIR) break;
              const berried = rng.nextInt(LUSH.BERRY_ONE_IN) === 0;
              chunk.setBlockState(lx, vy, lz, berried ? lush.caveVinesBerries : lush.caveVines, () => false);
            }
          }
        }
      }
    }
  }

  /**
   * Sink a shallow clay basin into a lush cave floor and fill it with water, with
   * dripleaf growing off the clay rim — the pool a big dripleaf needs to exist at
   * all, and the reason a lush cave reads as a garden rather than a mossy tunnel.
   *
   * The basin is dug into the FLOOR (one course down), so the water sits in a
   * depression rather than spilling across the cavern. It never touches the E-6
   * invariant: the carve has already finished by the time this runs, so no carve
   * decision can see this water — the same reason the aquifer pass is its own pass.
   */
  private sinkClayPool(
    chunk: Chunk, lx: number, y: number, lz: number,
    lush: NonNullable<NonNullable<CaveFeatures['ids']['lush']>>, rng: Rng,
  ): void {
    const water = this.features?.ids.water;
    const r = LUSH.CLAY_POOL_RADIUS;
    for (let dz = -r; dz <= r; dz++) {
      for (let dx = -r; dx <= r; dx++) {
        const px = lx + dx, pz = lz + dz;
        if (px < 0 || px >= WORLD.SECTION || pz < 0 || pz >= WORLD.SECTION) continue;
        if (dx * dx + dz * dz > r * r) continue;
        if (!chunk.sections[sectionIndexForY(y - 2)]) continue;
        // The floor cell must be carvable stone (or the moss this pass just laid
        // over it — including the pool's OWN centre cell, which is always moss by
        // the time we get here). Anything else means an ore vein, a structure or
        // bedrock generated at this cell, and a pool has no business punching
        // through it.
        const floor = chunk.getBlockState(px, y - 1, pz);
        if (this.carvable[floor] !== 1 && floor !== lush.moss) continue;
        if (chunk.getBlockState(px, y, pz) !== STATE_AIR) continue;
        const rim = dx * dx + dz * dz > (r - 1) * (r - 1);
        if (rim) {
          // The rim stays at floor level: clay you can stand on, sometimes leafed.
          chunk.setBlockState(px, y - 1, pz, lush.clay!, () => true);
          if (lush.smallDripleaf !== undefined && rng.nextInt(LUSH.DRIPLEAF_ONE_IN) === 0) {
            const big = lush.bigDripleaf !== undefined && rng.nextInt(LUSH.BIG_DRIPLEAF_ONE_IN) === 0;
            chunk.setBlockState(px, y, pz, big ? lush.bigDripleaf! : lush.smallDripleaf, () => false);
          }
        } else if (this.carvable[chunk.getBlockState(px, y - 2, pz)] === 1 || chunk.getBlockState(px, y - 2, pz) === lush.moss) {
          // The middle sinks one course into a clay bed and holds the water.
          chunk.setBlockState(px, y - 2, pz, lush.clay!, () => true);
          chunk.setBlockState(px, y - 1, pz, water ?? lush.clay!, () => false);
        }
      }
    }
  }
}
